import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, User, Users, Video, Film, MessageCircle, Calendar, Award } from 'lucide-react';
import Nav from '../components/nav';
import SEO from '../components/SEO';

const CastingAssistantPage = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [particles, setParticles] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  
  // Generate animated background particles
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 40; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 5 + 1,
          speedX: (Math.random() - 0.5) * 0.3,
          speedY: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.5 + 0.1
        });
      }
      setParticles(newParticles);
    };
    
    generateParticles();
    
    // Animation loop for particles
    const animateParticles = () => {
      setParticles(prevParticles => 
        prevParticles.map(particle => ({
          ...particle,
          x: (particle.x + particle.speedX + 100) % 100,
          y: (particle.y + particle.speedY + 100) % 100
        }))
      );
      requestAnimationFrame(animateParticles);
    };
    
    const animationId = requestAnimationFrame(animateParticles);
    return () => cancelAnimationFrame(animationId);
  }, []);
  
  // Handle scroll to track position
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Calculate which stage is in view
      const stageElements = document.querySelectorAll('.stage-content');
      
      stageElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        if (rect.top <= 300 && rect.bottom >= 300) {
          setActiveStage(index);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleTryCastingClick = () => {
    setShowLoader(true);
    setTimeout(() => {
      window.location.href = '/services';
    }, 3300);
  };

  const stages = [
    {
      title: "Define Your Project",
      icon: <Film className="h-8 w-8 text-white" />,
      content: `Start your casting journey by defining your project's specific requirements. Whether you're producing a feature film, a short film, a TV series, or an advertisement, the Casting Assistant AI needs to understand the scope and nature of your production.

Begin by entering key project details such as the genre, duration, production timeline, and budget constraints. The more specific information you provide, the better the AI can match you with suitable talent. Are you creating a period drama that requires actors with particular looks? Maybe a sci-fi adventure needing performers with specific skills? Perhaps a commercial requiring certain demographics?

This initial step is crucial as it sets the foundation for the entire casting process. The Casting Assistant AI uses advanced algorithms to analyze your project requirements and cross-reference them with its vast database of talent. By clearly articulating your vision at this stage, you're ensuring that all subsequent recommendations will align perfectly with your creative and budgetary needs.

Producer Buddy's Casting Assistant AI stands apart from traditional casting methods by eliminating the need for lengthy manual searches and preliminary auditions. The system is designed to understand not just the explicit requirements you state, but also the implicit needs of your production based on similar successful projects in the past.`,
      imageAlt: "Project definition interface showing genre, timeline and budget inputs",
      bgColor: "from-indigo-600 to-purple-800"
    },
    {
      title: "AI Analysis & Matching",
      icon: <Award className="h-8 w-8 text-white" />,
      content: `Once you've defined your project, Casting Assistant AI's sophisticated algorithms go to work. The system analyzes your requirements against its comprehensive database of actors, actresses, supporting cast, and crew members.

The AI doesn't just match based on availability and basic criteria – it performs a deep analysis of each talent's previous work, their specific skills, physical attributes, performance style, and audience reception. This intelligent matching system considers subtleties that would take human casting directors weeks to assess.

Pilomation's proprietary AI technology evaluates thousands of potential matches in seconds, considering factors like on-screen chemistry between potential cast members, previous successful collaborations, and alignment with your project's artistic vision. The system even analyzes subtle performance nuances from previous work to determine compatibility with your project's tone and style.

During this stage, the AI also considers practical aspects such as scheduling conflicts, geographic location, and budget alignment. This prevents you from pursuing talent that might be unavailable or outside your budget range, saving valuable time in the casting process.

The system continuously learns from successful productions, incorporating feedback to refine its matching algorithms. This means that as more filmmakers use Producer Buddy, the recommendations become increasingly precise and tailored to your specific needs.`,
      imageAlt: "AI analyzing profiles and matching with project requirements visualization",
      bgColor: "from-blue-600 to-cyan-800"
    },
    {
      title: "Review Personalized Recommendations",
      icon: <Users className="h-8 w-8 text-white" />,
      content: `Now comes the exciting part – reviewing your personalized talent recommendations! Casting Assistant AI presents you with a curated selection of actors, actresses, and crew members who best match your project requirements.

Each recommendation includes comprehensive profiles featuring professional experience, previous roles, performance styles, special skills, and availability. You'll see highlighted attributes explaining why each individual was recommended for your specific project – whether it's their expertise in a particular genre, their physical characteristics, or their proven ability to perform certain types of scenes.

The recommendations are organized into intuitive categories based on roles and departments, making it easy to navigate through leading actors, supporting cast, and technical crew. Each profile contains professional headshots, video reels, and portfolio highlights so you can immediately assess visual compatibility with your project.

The system also provides insights into each talent's work history, including productions they've been part of, directors they've worked with, and any awards or recognitions they've received. This gives you valuable context about their experience and industry standing.

What sets Producer Buddy's Casting Assistant AI apart is the way it surfaces hidden gems – talented individuals who might not be on your radar but perfectly match your project needs. This includes emerging talents whose skills align exceptionally well with your requirements, helping you discover fresh faces while minimizing casting risks.`,
      imageAlt: "Talent profiles showing headshots, experience, and role recommendations",
      bgColor: "from-teal-600 to-green-700"
    },
    {
      title: "Budget Optimization",
      icon: <Award className="h-8 w-8 text-white" />,
      content: `Managing your production budget effectively is critical, and Casting Assistant AI makes this process seamless. The platform provides transparent budget forecasting for each potential cast member, allowing you to make informed decisions that balance artistic needs with financial realities.

The system offers filtering options to view talent recommendations within specific budget ranges, ensuring you only review options that align with your financial parameters. This prevents the frustration of finding perfect talent only to discover they're outside your budget constraints.

Producer Buddy's Casting Assistant AI goes beyond simple price tags – it provides sophisticated budget optimization suggestions that consider the overall value each talent brings to your production. The AI analyzes factors such as audience draw, social media presence, and marketing potential alongside performance capabilities to determine the potential return on investment for key casting choices.

For projects with tight budgets, the system can suggest talent packages – combinations of cast members who have worked well together previously and might be available at favorable rates when booked as a group. This innovative approach can sometimes unlock opportunities to work with higher-profile talent than would otherwise be possible.

The budget optimization tools also provide alternatives at different price points, allowing you to see how adjusting your casting budget might impact other aspects of production. This holistic approach to budget management ensures you're making the most efficient use of your resources across the entire production.`,
      imageAlt: "Budget optimization interface showing cost comparisons and talent alternatives",
      bgColor: "from-amber-500 to-orange-700"
    },
    {
      title: "Direct Communication",
      icon: <MessageCircle className="h-8 w-8 text-white" />,
      content: `Once you've identified promising talent, Casting Assistant AI streamlines the communication process. The platform's built-in messaging system allows you to directly contact actors, actresses, and crew members without leaving the application.

This integrated communication hub eliminates the need to juggle multiple email chains, phone calls, or third-party messaging apps. All conversations are centralized in one secure location, creating a clear record of all interactions and agreements for future reference.

The messaging system includes templates for common casting communications – such as role offers, audition requests, and availability checks – which can be personalized to suit your specific needs. This saves valuable time while maintaining a professional approach to talent outreach.

For busy production teams, the platform offers scheduling assistance, automatically suggesting meeting times based on the availability information in talent profiles. Calendar integration allows for seamless booking of auditions, meetings, or callbacks directly through the platform.

Producer Buddy's communication system also supports file sharing, enabling you to securely send scripts, NDA documents, character breakdowns, and other materials directly to potential cast members. This ensures all relevant information is exchanged efficiently within a protected environment.

The platform's notification system keeps all parties informed about new messages, scheduled meetings, or pending decisions, ensuring smooth communication flow throughout the casting process. This responsive system eliminates delays and miscommunications that often plague traditional casting workflows.`,
      imageAlt: "In-app messaging interface showing conversation with potential cast member",
      bgColor: "from-pink-500 to-rose-700"
    },
    {
      title: "Scheduling & Booking",
      icon: <Calendar className="h-8 w-8 text-white" />,
      content: `The Casting Assistant AI transforms the traditionally complex process of scheduling and booking talent into a streamlined, efficient workflow. Once you've selected your preferred candidates, the platform's scheduling tools take over to coordinate availability and confirm bookings.

The system's intelligent calendar function cross-references the availability of all selected talent, instantly identifying optimal shooting schedules that accommodate everyone's commitments. This eliminates the time-consuming back-and-forth typically required to align multiple schedules.

Producer Buddy's booking system generates professional, standardized contracts and agreements that comply with industry regulations and protect all parties involved. These documents can be customized to reflect your specific project requirements and production standards.

The platform supports digital signature functionality, allowing for paperless contract execution. This accelerates the booking process significantly, reducing what traditionally took weeks to just days or even hours in some cases.

For productions with complex scheduling needs, the AI offers scenario planning tools that can simulate different shooting schedules based on various talent combinations. This allows you to visualize how different casting choices might impact your overall production timeline.

Once bookings are confirmed, the system automatically generates comprehensive production schedules that can be shared with all relevant parties. These schedules include detailed information about shooting locations, call times, scene requirements, and other essential details.`,
      imageAlt: "Calendar interface showing shooting schedule and talent availability",
      bgColor: "from-violet-600 to-purple-800"
    },
    {
      title: "Project Initialization",
      icon: <Video className="h-8 w-8 text-white" />,
      content: `With your cast and crew selected, scheduled, and booked, Casting Assistant AI seamlessly transitions to project initialization. This final stage prepares everything for a smooth production launch, centralizing all the casting information in an accessible format for your entire production team.

The platform generates comprehensive cast and crew directories with all relevant contact information, role assignments, and important notes. These directories can be exported in various formats to integrate with other production management tools your team might be using.

Producer Buddy's system creates detailed character portfolios for directors and performers, including character backgrounds, personality traits, and relationship dynamics. These resources help ensure consistent character interpretation across the production.

For crew members, the platform provides role-specific briefings based on the project parameters you established in the initial stages. This ensures everyone understands the project vision, technical requirements, and artistic direction before production begins.

The system also facilitates pre-production meetings by suggesting agenda items based on your casting choices and project needs. This helps ensure all critical topics are addressed before cameras start rolling.

As production begins, Casting Assistant AI continues to provide value through its performance tracking tools, allowing directors and producers to make notes on each talent's contributions. This creates valuable data for future casting decisions and helps maintain quality control throughout the production process.

By streamlining the entire casting workflow from concept to production, Pilomation's Producer Buddy eliminates the traditional inefficiencies of talent selection and management. What once required multiple agencies, countless hours, and significant resources now happens within a single, intelligent platform.`,
      imageAlt: "Project dashboard showing cast, crew, and production details ready for filming",
      bgColor: "from-blue-800 to-indigo-900"
    }
  ];

  return (
    <>
    <SEO
      title="Casting How-To Guide"
      description="A step-by-step guide to finding, auditing, and locking cast members using ProducerBuddy's AI-assisted casting tools."
      url="https://producerbuddy.pilomation.com/castHowTo"
    />
    <Nav />
    <div className="relative min-h-screen overflow-hidden bg-gray-900 text-white">
      {/* Animated background particles */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        {particles.map(particle => (
          <div 
            key={particle.id}
            className="absolute rounded-full bg-white"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
              boxShadow: `0 0 ${particle.size * 2}px rgba(255, 255, 255, 0.8)`,
              transition: 'top 0.5s ease, left 0.5s ease'
            }}
          />
        ))}
      </div>
      
      {/* Fixed geometric shapes */}
      <div 
        className="fixed w-64 h-64 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 blur-3xl opacity-20" 
        style={{ top: '15%', left: '10%', transform: `translateY(${scrollY * 0.1}px)` }}
      ></div>
      <div 
        className="fixed w-96 h-96 rounded-full bg-gradient-to-r from-blue-500 to-teal-500 blur-3xl opacity-20" 
        style={{ top: '50%', right: '5%', transform: `translateY(${-scrollY * 0.15}px)` }}
      ></div>
      <div 
        className="fixed w-80 h-80 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 blur-3xl opacity-20" 
        style={{ bottom: '10%', left: '20%', transform: `translateY(${-scrollY * 0.08}px)` }}
      ></div>

      {/* Header with branding */}
      <header className="relative z-10 py-6 px-4 bg-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Casting Assistant AI</h1>
              <p className="text-gray-300">A Producer Buddy Tool by Pilomation</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero section */}
      <section className="relative z-10 py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-pink-500">
            Revolutionize Your Casting Process
          </h2>
          <p className="text-xl mb-10 max-w-3xl mx-auto text-gray-200">
            From concept to camera-ready in record time. Discover the power of AI-driven casting for films, TV shows, and advertisements.
          </p>
          <button 
            onClick={handleTryCastingClick}
            className="relative group overflow-hidden bg-white bg-opacity-10 backdrop-blur-lg hover:bg-opacity-20 text-white border border-white border-opacity-20 px-10 py-4 rounded-full font-bold text-lg transition-all duration-300"
          >
            <span className="relative z-10">Start Casting Now</span>
            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-cyan-500 to-purple-500 opacity-0 group-hover:opacity-50 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></span>
          </button>
        </div>
      </section>

      {/* Main content with stages */}
      <section className="relative z-10 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Left sidebar with stage navigation */}
            <div className="md:w-1/4">
              <div className="sticky top-8">
                <h3 className="text-2xl font-bold text-white mb-6">Casting Process</h3>
                <div className="relative">
                  {/* Animated line connecting stages */}
                  <div className="absolute left-4 top-0 bottom-0 w-1 bg-gray-700"></div>
                  
                  {/* Animated active line */}
                  <div 
                    className="absolute left-4 top-0 w-1 bg-gradient-to-b from-cyan-400 to-purple-500 transition-all duration-500"
                    style={{ 
                      height: `${((activeStage + 1) / stages.length) * 100}%`,
                      maxHeight: '100%'
                    }}
                  ></div>
                  
                  {/* Stage bubbles */}
                  <div className="space-y-8">
                    {stages.map((stage, index) => (
                      <div 
                        key={index}
                        className="flex items-center cursor-pointer group"
                        onClick={() => {
                          document.querySelectorAll('.stage-content')[index].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                          });
                        }}
                      >
                        <div className={`z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${
                          activeStage >= index 
                            ? 'bg-gradient-to-r from-cyan-400 to-purple-500 text-white' 
                            : 'bg-gray-800 text-gray-400 group-hover:text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div className={`ml-4 font-medium transition-all duration-300 ${
                          activeStage === index ? 'text-white scale-105 origin-left' : 'text-gray-400 group-hover:text-gray-200'
                        }`}>
                          {stage.title}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right content area with stages */}
            <div className="md:w-3/4">
              <div className="space-y-48">
                {stages.map((stage, index) => (
                  <div key={index} className="stage-content scroll-mt-32">
                    <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-r ${stage.bgColor} p-1`}>
                      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDIwIDAgTCAwIDAgTCAwIDIwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wMykiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
                      
                      <div className="relative z-10 rounded-xl overflow-hidden bg-gray-900 bg-opacity-70 backdrop-blur-sm">
                        {/* Header */}
                        <div className="p-6 border-b border-gray-800">
                          <div className="flex items-center">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-400 to-purple-500 flex items-center justify-center">
                              {stage.icon}
                            </div>
                            <div className="ml-4">
                              <h3 className="text-2xl font-bold text-white">
                                Stage {index + 1}: {stage.title}
                              </h3>
                            </div>
                          </div>
                        </div>
                        
                        {/* Image representation */}
                        <div className="relative h-64 bg-gray-800">
                          {/* Glowing effect on hover */}
                          <div 
                            className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-purple-600 opacity-0 hover:opacity-20 transition-opacity duration-500"
                          ></div>
                          
                          {/* Background pattern */}
                          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOSAxLjc5MS00IDQtNHM0IDEuNzkxIDQgNC0xLjc5MSA0LTQgNC00LTEuNzkxLTQtNHptMC0zMGMwLTIuMjA5IDEuNzkxLTQgNC00czQgMS43OTEgNCA0LTEuNzkxIDQtNCA0LTQtMS43OTEtNC00em0wIDYwYzAtMi4yMDkgMS43OTEtNCA0LTRzNCAxLjc5MSA0IDQtMS43OTEgNC00IDQtNC0xLjc5MS00LTR6bS0zMCAwYzAtMi4yMDkgMS43OTEtNCA0LTRzNCAxLjc5MSA0IDQtMS43OTEgNC00IDQtNC0xLjc5MS00LTR6bTAtMzBjMC0yLjIwOSAxLjc5MS00IDQtNHM0IDEuNzkxIDQgNC0xLjc5MSA0LTQgNC00LTEuNzkxLTQtNHptMC0zMGMwLTIuMjA5IDEuNzkxLTQgNC00czQgMS43OTEgNCA0LTEuNzkxIDQtNCA0LTQtMS43OTEtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-5"></div>
                          
                          <div className="relative h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                                {stage.icon}
                              </div>
                              <p className="text-gray-400">{stage.imageAlt}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-8">
                          <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                            {stage.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Final CTA */}
              <div className="mt-32 mb-16 text-center">
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-500 mb-6">
                  Ready to Transform Your Casting Process?
                </h3>
                <button 
                  onClick={handleTryCastingClick}
                  className="relative group overflow-hidden bg-gradient-to-r from-cyan-500 to-purple-500 px-10 py-4 rounded-full font-bold text-xl hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300"
                >
                  <span className="relative z-10">Try Casting with AI</span>
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-purple-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Scroll indicators */}
      <div className="fixed bottom-8 right-8 flex flex-col space-y-4 z-20">
        <button 
          className="relative group w-12 h-12 rounded-full bg-black bg-opacity-30 backdrop-blur-lg border border-white border-opacity-10 flex items-center justify-center hover:bg-opacity-50 transition-all duration-300"
          onClick={() => window.scrollBy({ top: -300, behavior: 'smooth' })}
        >
          <ChevronUp className="w-6 h-6 text-white" />
          <div className="absolute inset-0 rounded-full border border-white border-opacity-20 transform scale-0 group-hover:scale-100 group-hover:opacity-0 transition-all duration-500"></div>
        </button>
        <button 
          className="relative group w-12 h-12 rounded-full bg-black bg-opacity-30 backdrop-blur-lg border border-white border-opacity-10 flex items-center justify-center hover:bg-opacity-50 transition-all duration-300"
          onClick={() => window.scrollBy({ top: 300, behavior: 'smooth' })}
        >
          <ChevronDown className="w-6 h-6 text-white" />
          <div className="absolute inset-0 rounded-full border border-white border-opacity-20 transform scale-0 group-hover:scale-100 group-hover:opacity-0 transition-all duration-500"></div>
        </button>
      </div>
      
      {/* Loading overlay with enhanced animation */}
      {showLoader && (
        <div className="fixed inset-0 bg-transparent bg-opacity-80 flex items-center justify-center z-50 backdrop-blur-md">
          <div className="relative">
            {/* Animated circles */}
            <div className="absolute top-1/2 left-1/2 w-32 h-32 border-2 border-cyan-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 animate-ping" style={{animationDuration: '2s'}}></div>
            <div className="absolute top-1/2 left-1/2 w-40 h-40 border-2 border-purple-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 animate-ping" style={{animationDuration: '2.5s'}}></div>
            
            <div className="text-center relative z-10 backdrop-blur-lg bg-black bg-opacity-30 p-8 rounded-2xl border border-white border-opacity-10">
              <div className="w-20 h-20 mx-auto mb-6 relative">
                <div className="absolute inset-0 rounded-full border-4 border-cyan-500 border-l-transparent animate-spin"></div>
                <div className="absolute inset-1 rounded-full border-4 border-purple-500 border-r-transparent animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
              </div>
              <p className="text-white text-xl mb-4">Loading your new casting experience...</p>
              <div className="w-64 h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 animate-loader" style={{width: '100%'}}></div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom animations */}
      <style jsx>{`
        @keyframes loader {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(0); }
        }
        .animate-loader {
          animation: loader 3s ease-in-out infinite;
        }
      `}</style>
    </div>
    </>
  );
};

export default CastingAssistantPage;
